<template>
  <section id="analytics" class="analytics-section">
    <v-container class="section-container">
      <div class="section-header text-center">
        <span class="section-tag">Панель управления OZO.OFFICE</span>
        <h2 class="section-title">Аналитический центр ресторатора</h2>
        <p class="section-description">
          Полная прозрачность финансовых потоков, работы зала, доставки и
          поведения каждого гостя. Принимайте управленческие решения на основе
          точных данных в режиме реального времени.
        </p>

        <!-- Переключатель вкладок дэшборда OZO.OFFICE (из слайдов 10 и 11) -->
        <div class="office-tabs mt-6">
          <button
            v-for="(tab, tIdx) in dashboardTabs"
            :key="tab.id"
            class="office-tab-btn"
            :class="{ active: activeTabIndex === tIdx }"
            @click="activeTabIndex = tIdx"
          >
            <i :class="[tab.icon, 'mr-1']"></i>
            <span>{{ tab.tabLabel }}</span>
          </button>
        </div>
      </div>

      <!-- Главный Showcase активного дэшборда с реальным скриншотом из презентации -->
      <transition name="fade" mode="out-in">
        <div :key="activeTab.id" class="office-showcase-box mt-8">
          <v-row align="center" justify="space-between">
            <v-col cols="12" lg="7" class="showcase-screen-col">
              <div class="screen-frame">
                <div class="browser-header">
                  <span class="dot dot-red"></span>
                  <span class="dot dot-yellow"></span>
                  <span class="dot dot-green"></span>
                  <span class="browser-url"
                    >https://office.ozo.direct/analytics/{{
                      activeTab.id
                    }}</span
                  >
                </div>
                <div class="screen-img-box img-skeleton-box">
                  <img
                    :src="activeTab.screenshot"
                    :alt="activeTab.title"
                    class="screen-img"
                    loading="lazy"
                    @load="onImgLoad"
                  />
                </div>
              </div>
            </v-col>

            <v-col cols="12" lg="5" class="showcase-details-col">
              <div class="tab-badge">{{ activeTab.category }}</div>
              <h3 class="tab-title">{{ activeTab.title }}</h3>
              <p class="tab-desc">{{ activeTab.description }}</p>

              <div class="tab-features-list">
                <div
                  v-for="(feat, fIdx) in activeTab.features"
                  :key="fIdx"
                  class="tab-feature-item"
                >
                  <i class="ri-checkbox-circle-fill feat-check"></i>
                  <span>{{ feat }}</span>
                </div>
              </div>

              <div class="tab-highlight-card mt-6">
                <div class="highlight-title">
                  {{ activeTab.highlightLabel }}
                </div>
                <div class="highlight-val">{{ activeTab.highlightValue }}</div>
                <p class="highlight-sub">{{ activeTab.highlightSub }}</p>
              </div>
            </v-col>
          </v-row>
        </div>
      </transition>

      <!-- Дополнительная сетка 3 ключевых аналитических виджетов -->
      <v-row class="dashboard-grid mt-10" align="stretch">
        <!-- Виджет 1: Выручка по каналам продаж -->
        <v-col cols="12" md="4">
          <div class="dash-card">
            <div class="dash-header">
              <span class="dash-title">Выручка по каналам продаж</span>
              <span class="dash-period">30 дней</span>
            </div>
            <div class="dash-value">₸ 14 850 000</div>
            <div class="dash-change text-positive">
              +18.4% к прошлому месяцу
            </div>

            <div class="channel-bars mt-6">
              <div class="channel-bar-item">
                <div class="channel-meta">
                  <span>В зале (QR-оплата)</span>
                  <strong>54%</strong>
                </div>
                <div class="bar-track">
                  <div class="bar-fill fill-green" style="width: 54%"></div>
                </div>
              </div>

              <div class="channel-bar-item">
                <div class="channel-meta">
                  <span>Собственная доставка</span>
                  <strong>32%</strong>
                </div>
                <div class="bar-track">
                  <div class="bar-fill fill-dark" style="width: 32%"></div>
                </div>
              </div>

              <div class="channel-bar-item">
                <div class="channel-meta">
                  <span>Самовывоз (навынос)</span>
                  <strong>14%</strong>
                </div>
                <div class="bar-track">
                  <div class="bar-fill fill-muted" style="width: 14%"></div>
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Виджет 2: RFM-анализ базы гостей -->
        <v-col cols="12" md="4">
          <div class="dash-card">
            <div class="dash-header">
              <span class="dash-title">RFM-модель сегментации</span>
              <span class="dash-period">LTV гостей</span>
            </div>
            <div class="dash-value">8 420 гостей</div>
            <div class="dash-change text-positive">
              73% активных покупателей
            </div>

            <div class="guest-segments mt-6">
              <div class="segment-box segment-vip">
                <div class="seg-title">Recency (Давность)</div>
                <div class="seg-stat">Когда гость делал последний заказ</div>
              </div>
              <div class="segment-box segment-promising">
                <div class="seg-title">Frequency (Частота)</div>
                <div class="seg-stat">Сколько визитов совершил за период</div>
              </div>
              <div class="segment-box segment-churn">
                <div class="seg-title">Monetary (Сумма)</div>
                <div class="seg-stat">
                  Общий чек и валовая ценность для заведения
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Виджет 3: ABC-анализ популярных блюд -->
        <v-col cols="12" md="4">
          <div class="dash-card">
            <div class="dash-header">
              <span class="dash-title">Популярные блюда</span>
              <span class="dash-period">ABC-анализ</span>
            </div>
            <div class="dash-value">3 840 заказов</div>
            <div class="dash-change text-positive">Топ-драйверы выручки</div>

            <div class="dish-list mt-6">
              <div class="dish-item">
                <div class="dish-info">
                  <span class="dish-name">Томлёные говяжьи рёбрышки</span>
                  <span class="dish-orders">520 заказов • Категория А</span>
                </div>
                <span class="dish-badge">Топ-1</span>
              </div>
              <div class="dish-item">
                <div class="dish-info">
                  <span class="dish-name">Паста с морепродуктами</span>
                  <span class="dish-orders">410 заказов • Категория А</span>
                </div>
                <span class="dish-badge">Топ-2</span>
              </div>
              <div class="dish-item">
                <div class="dish-info">
                  <span class="dish-name">Фирменный лимонад OZO</span>
                  <span class="dish-orders">910 порций • Маржа 82%</span>
                </div>
                <span class="dish-badge highlight">Высокая маржа</span>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
export default {
  name: "AnalyticsSection",
  data() {
    return {
      activeTabIndex: 0,
      dashboardTabs: [
        {
          id: "revenue",
          tabLabel: "Выручка и каналы",
          icon: "ri-line-chart-line",
          category: "Финансовый контроль",
          title: "Выручка по каналам: зал, доставка и навынос",
          screenshot: require("@/assets/presentation/office-revenue.png"),
          description:
            "Консолидируйте все потоки заказов в одном месте. Видите реальную маржинальность каждого канала продаж без потери данных.",
          features: [
            "Раздельный учет выручки по залу, самовывозу и доставке",
            "Сравнение показателей по дням, неделям и месяцам",
            "Автоматический экспорт отчетов для бухгалтерии",
          ],
          highlightLabel: "Прозрачность финансов",
          highlightValue: "100%",
          highlightSub: "Прямые поступления на расчетный счет заведения",
        },
        {
          id: "rfm",
          tabLabel: "RFM-сегменты",
          icon: "ri-user-star-line",
          category: "Удержание гостей",
          title: "Глубокая RFM-сегментация базы гостей",
          screenshot: require("@/assets/presentation/office-rfm.png"),
          description:
            "Узнайте, кто ваши ключевые гости, кто рискует уйти к конкурентам, и кого нужно простимулировать персональным купоном.",
          features: [
            "Автоматическое разделение гостей на группы по частоте и чеку",
            "Запуск триггерных маркетинговых пушей на «спящих» клиентов",
            "Отслеживание динамики среднего чека постоянных гостей",
          ],
          highlightLabel: "Возврат «спящих» гостей",
          highlightValue: "+25%",
          highlightSub: "Рост повторных заказов благодаря триггерным акциям",
        },
        {
          id: "dishes",
          tabLabel: "Популярные блюда",
          icon: "ri-restaurant-2-line",
          category: "Аналитика меню",
          title: "ABC-анализ блюд и прибыльности кухни",
          screenshot: require("@/assets/presentation/office-dishes.png"),
          description:
            "Контролируйте, какие позиции приносят максимум маржи, а какие пора вывести из меню или заменить сезонными новинками.",
          features: [
            "Рейтинг блюд по выручке, маржинальности и количеству порций",
            "Отслеживание стоп-листов и скорости отдачи кухни",
            "Оптимизация себестоимости и закупа продуктов",
          ],
          highlightLabel: "Контроль маржинальности",
          highlightValue: "ABC",
          highlightSub: "Автоматическая классификация позиций меню",
        },
        {
          id: "reviews",
          tabLabel: "Отзывы и сервис",
          icon: "ri-star-smile-line",
          category: "Качество сервиса",
          title: "Управление лояльностью и отзывами гостей",
          screenshot: require("@/assets/presentation/office-reviews.png"),
          description:
            "Получайте обратную связь сразу после оплаты или закрытия чека. Реагируйте на замечания гостей до того, как они попадут в соцсети.",
          features: [
            "Мгновенные оценки блюд и обслуживания в приложении",
            "Возможность быстро отправить гостю извинительный купон",
            "Рейтинг официантов и смен по оценкам гостей",
          ],
          highlightLabel: "Индекс удовлетворенности",
          highlightValue: "4.9 / 5.0",
          highlightSub: "Средний показатель ресторанов сети OZO",
        },
      ],
    };
  },
  computed: {
    activeTab() {
      return this.dashboardTabs[this.activeTabIndex] || this.dashboardTabs[0];
    },
  },
  mounted() {
    this.checkImagesLoaded();
  },
  updated() {
    this.$nextTick(() => {
      this.checkImagesLoaded();
    });
  },
  methods: {
    onImgLoad(e) {
      if (e && e.target) {
        e.target.classList.add("is-loaded");
        if (e.target.parentElement) {
          e.target.parentElement.classList.add("is-loaded");
        }
      }
    },
    checkImagesLoaded() {
      this.$nextTick(() => {
        this.$el.querySelectorAll(".screen-img").forEach((img) => {
          if (img.complete && img.naturalWidth > 0) {
            img.classList.add("is-loaded");
            if (img.parentElement) img.parentElement.classList.add("is-loaded");
          }
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/_variables.scss";

.analytics-section {
  background-color: $color-bg-light;
  padding: $section-py 0;

  @media (max-width: 960px) {
    padding: $section-py-mobile 0;
  }
}

.section-container {
  max-width: $container-max-width;
  margin: 0 auto;
}

.section-tag {
  display: inline-block;
  color: #0b7a3a;
  background: $color-neon-green-soft;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 6px 14px;
  border-radius: $radius-full;
  margin-bottom: 12px;
}

.section-title {
  font-family: $font-family-heading;
  font-size: 38px;
  font-weight: 800;
  color: $color-text-dark;
  margin-bottom: 16px;

  @media (max-width: 960px) {
    font-size: 28px;
  }
}

.section-description {
  font-size: 16px;
  color: $color-text-secondary;
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.6;
}

/* Office Tabs */
.office-tabs {
  display: inline-flex;
  background: #e2e8f0;
  padding: 4px;
  border-radius: $radius-full;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 480px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
    border-radius: $radius-md;
    width: 100%;
  }

  .office-tab-btn {
    border: none;
    background: transparent;
    padding: 9px 20px;
    border-radius: $radius-full;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: all 0.25s ease;

    @media (max-width: 600px) {
      padding: 7px 12px;
      font-size: 12px;
    }

    @media (max-width: 480px) {
      border-radius: $radius-sm;
      padding: 8px 6px;
      font-size: 11px;
      text-align: center;
    }

    &.active {
      background: #ffffff;
      color: $color-text-dark;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      font-weight: 700;
    }
  }
}

/* Showcase Box */
.office-showcase-box {
  background: #ffffff;
  border-radius: $radius-lg;
  border: 1px solid #e2e8f0;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
  padding: 36px;

  @media (max-width: 960px) {
    padding: 24px 16px;
  }

  @media (max-width: 600px) {
    padding: 18px 12px;
  }

  @media (max-width: 480px) {
    padding: 14px 10px;
  }
}

.screen-frame {
  background: #1e293b;
  border-radius: $radius-md;
  overflow: hidden;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.18);
  border: 1px solid #334155;

  .browser-header {
    background: #0f172a;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    gap: 8px;

    .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;

      &-red {
        background: #ef4444;
      }
      &-yellow {
        background: #f59e0b;
      }
      &-green {
        background: #10b981;
      }
    }

    .browser-url {
      font-size: 11px;
      color: #94a3b8;
      margin-left: 8px;
      font-family: monospace;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 240px;

      @media (max-width: 480px) {
        max-width: 150px;
      }
    }
  }

  .screen-img-box {
    width: 100%;
    min-height: 240px;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .screen-img {
    width: 100%;
    height: auto;
    max-height: 380px;
    object-fit: contain;
    display: block;
    background: #ffffff;
  }
}

.showcase-details-col {
  padding-left: 20px;

  @media (max-width: 960px) {
    // padding-left: 0;
    margin-top: 24px;
  }

  .tab-badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #0b7a3a;
    background: $color-neon-green-soft;
    padding: 4px 12px;
    border-radius: $radius-full;
    margin-bottom: 12px;
  }

  .tab-title {
    font-family: $font-family-heading;
    font-size: 24px;
    font-weight: 800;
    color: $color-text-dark;
    margin-bottom: 10px;
    line-height: 1.25;
  }

  .tab-desc {
    font-size: 14px;
    line-height: 1.6;
    color: $color-text-secondary;
    margin-bottom: 18px;
  }

  .tab-features-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;

    .tab-feature-item {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      font-size: 13px;
      color: $color-text-dark;

      .feat-check {
        color: #0ebd60;
        font-weight: 800;
        flex-shrink: 0;
      }
    }
  }

  .tab-highlight-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-left: 4px solid #00e676;
    border-radius: $radius-sm;
    padding: 14px 18px;

    .highlight-title {
      font-size: 12px;
      font-weight: 600;
      color: #64748b;
    }

    .highlight-val {
      font-family: $font-family-heading;
      font-size: 26px;
      font-weight: 900;
      color: #0b7a3a;
      line-height: 1.1;
      margin: 4px 0;
    }

    .highlight-sub {
      font-size: 12px;
      color: #475569;
      margin: 0;
    }
  }
}

/* 3 Widgets Grid */
.dash-card {
  background: $color-bg-white;
  border: 1px solid #e2e8f0;
  border-radius: $radius-lg;
  padding: 32px 28px;
  box-shadow: $shadow-card;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: $shadow-hover;
    border-color: rgba(0, 230, 118, 0.4);
  }
}

.dash-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  .dash-title {
    font-size: 14px;
    font-weight: 600;
    color: $color-text-secondary;
  }

  .dash-period {
    font-size: 12px;
    font-weight: 600;
    background: #f1f5f9;
    padding: 4px 10px;
    border-radius: $radius-full;
    color: #475569;
  }
}

.dash-value {
  font-family: $font-family-heading;
  font-size: 30px;
  font-weight: 800;
  color: $color-text-dark;
  line-height: 1.1;
  margin-bottom: 6px;
}

.dash-change {
  font-size: 13px;
  font-weight: 600;

  &.text-positive {
    color: #0b7a3a;
  }
}

/* Bars */
.channel-bars {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.channel-bar-item {
  .channel-meta {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    margin-bottom: 6px;
    color: $color-text-dark;
  }

  .bar-track {
    height: 8px;
    background: #f1f5f9;
    border-radius: 4px;
    overflow: hidden;

    .bar-fill {
      height: 100%;
      border-radius: 4px;

      &.fill-green {
        background: #00e676;
      }
      &.fill-dark {
        background: #1e293b;
      }
      &.fill-muted {
        background: #94a3b8;
      }
    }
  }
}

/* Guest segments */
.guest-segments {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .segment-box {
    padding: 10px 14px;
    border-radius: $radius-sm;
    background: #f8fafc;
    border-left: 4px solid #cbd5e1;

    .seg-title {
      font-size: 13px;
      font-weight: 700;
      color: $color-text-dark;
    }

    .seg-stat {
      font-size: 12px;
      color: $color-text-secondary;
      margin-top: 2px;
    }

    &.segment-vip {
      border-left-color: #00e676;
      background: #f0fdf4;
    }

    &.segment-promising {
      border-left-color: #3b82f6;
      background: #eff6ff;
    }

    &.segment-churn {
      border-left-color: #f59e0b;
      background: #fffbeb;
    }
  }
}

/* Dish list */
.dish-list {
  display: flex;
  flex-direction: column;
  gap: 14px;

  .dish-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
    border-bottom: 1px solid #f1f5f9;

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    .dish-info {
      display: flex;
      flex-direction: column;

      .dish-name {
        font-size: 13px;
        font-weight: 600;
        color: $color-text-dark;
      }

      .dish-orders {
        font-size: 11px;
        color: $color-text-secondary;
      }
    }

    .dish-badge {
      font-size: 11px;
      font-weight: 700;
      padding: 4px 8px;
      border-radius: $radius-sm;
      background: #f1f5f9;
      color: #475569;

      &.highlight {
        background: rgba(0, 230, 118, 0.15);
        color: #0b7a3a;
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
